// InvestmentReturns.tsx
import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll } from "framer-motion";
import Lottie from "react-lottie";
import { useInView } from "react-intersection-observer";

import Graph1 from "@/assets/New-Graph.json";
import Graph2 from "@/assets/Phone.json";
import Strikeoff from "@/assets/Strikeoff.svg";
import scrolldown from "@/assets/ScrollDown2.gif";

// ------------------------------ CONFIG ------------------------------
const GROWTH_VALUE = 31;
const DISCOUNT_VALUE = 95000;
const ORIGINAL_VALUE = 100000;

export const InvestmentReturns = () => {
  const [frame, setFrame] = useState(0);

  const [growthValue, setGrowthValue] = useState(0);
  const [discountValue, setDiscountValue] = useState(0);

  const { ref: cardRef, inView } = useInView({
    threshold: 0.55,
    triggerOnce: false,
  });

  const sectionRef = useRef<HTMLDivElement | null>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // ------------------------ FRAME ENGINE ------------------------------
  useEffect(() => {
    return scrollYProgress.on("change", (p) => {
      setFrame(p < 0.2 ? 0 : 1);
    });
  }, []);

  // -------------------- GROWTH VALUE ANIMATION ------------------------
  useEffect(() => {
    if (frame !== 0 || !inView) return setGrowthValue(GROWTH_VALUE);

    setGrowthValue(0);
    let num = 0;

    const interval = setInterval(() => {
      num++;
      setGrowthValue(num);
      if (num >= GROWTH_VALUE) clearInterval(interval);
    }, 40);

    return () => clearInterval(interval);
  }, [frame, inView]);

  // -------------------- PRICE VALUE ANIMATION -------------------------
  useEffect(() => {
    if (frame !== 1) return setDiscountValue(0);

    let current = 0;
    const step = Math.ceil(DISCOUNT_VALUE / 20);

    const interval = setInterval(() => {
      current += step;
      if (current >= DISCOUNT_VALUE) {
        current = DISCOUNT_VALUE;
        clearInterval(interval);
      }
      setDiscountValue(current);
    }, 50);

    return () => clearInterval(interval);
  }, [frame]);

  // ======================= FADE VARIANTS ==========================
  const fade = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  // ---------------------------- JSX -----------------------------------
  return (
    <section
      ref={sectionRef}
      className="relative w-full"
      style={{ height: "250vh" }}
    >
      <div className="sticky top-[12vh] md:top-[18vh] w-full">

        {/* DESKTOP FIX APPLIED HERE */}
        <motion.div
          ref={cardRef}
          className="
            bg-[#1D1D21] 
            rounded-2xl 
            w-full 
            mx-auto 
            h-[80vh]              /* MOBILE SAME */
            md:min-h-[75vh]       /* DESKTOP CAN EXPAND */
            md:h-auto             /* ALLOW GROWTH IN HEIGHT */
            overflow-visible      /* FIX CLIPPING */
          "
        >
          <div className="flex flex-col md:grid md:grid-cols-2 h-full">

            {/* -------------------------------------------------------- */}
            {/* LEFT COLUMN - TEXT                                      */}
            {/* -------------------------------------------------------- */}
            <div
              className="
                flex flex-col 
                justify-between 
                md:justify-between    /* FIX DESKTOP CLIPPING */
                gap-6                 /* FIX DESKTOP SPACING */
                px-5 md:px-10 
                pt-6 pb-0 md:pb-4
              "
            >
              {/* FRAME 0 CONTENT */}
              {frame === 0 && (
                <motion.div
                  key="frame0"
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={fade}
                  className="flex flex-col justify-between h-full md:h-5/6"
                >
                  <div>
                    <h2 className="text-white text-2xl md:text-4xl font-semibold leading-tight">
                      Supercharge Your <br /> Investment Returns
                    </h2>

                    <div className="bg-[#DBAD71] h-[2px] w-[180px] my-3" />

                    <p className="text-white/70 text-sm max-w-md">
                      Investing in MFs not only grows your wealth it also
                      unlocks perks you don’t get anywhere else.
                    </p>
                  </div>

                  <div>
                    <p className="text-white text-lg">Growth In Last 2 Yrs</p>
                    <p className="text-[#DBAD71] text-6xl font-bold">
                      {growthValue}%
                    </p>
                    <p className="text-white/60 text-xs mt-2">
                      * This data is gathered from historic index fund returns
                    </p>
                    {/* SCROLL SECTION - Positioned at bottom aligned with right image */}
                    <div className="md:mt-0 lg:pt-0">
                      <div className="w-full flex flex-row items-center justify-start lg:justify-between gap-3">
                        <div className="flex items-center justify-center">
                          <span className="text-white text-xs lg:text-base whitespace-nowrap mr-1 mt-2 ml-2" style={{fontSize:10}}>Scroll</span>
                          <img src={scrolldown} className="w-4 h-4 lg:w-4 lg:h-4 bg-transparent mr-8 mt-2" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* FRAME 1 CONTENT */}
              {frame === 1 && (
                <motion.div
                  key="frame1"
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={fade}
                  className="flex flex-col justify-between md:justify-around h-full md:h-5/6"
                >
                  <div>
                    <h2 className="text-white text-2xl md:text-4xl font-semibold leading-tight">
                      And Unlock Discounts + <br /> Zero Cost EMIs
                    </h2>

                    <div className="bg-[#DBAD71] h-[2px] w-[180px] my-3" />

                    <p className="text-white/70 text-sm max-w-md">
                      Pledge your Mutual Funds and instantly unlock a sweet
                      discount. Same phone, same specs, just lighter on your
                      wallet.
                    </p>
                  </div>

                  <div className="space-y-2">
                    {/* ORIGINAL PRICE */}
                    <div className="relative inline-block">
                      <p className="text-white text-4xl font-bold opacity-60">
                        ₹{ORIGINAL_VALUE.toLocaleString("en-IN")}
                      </p>

                      <motion.img
                        src={Strikeoff}
                        className="absolute top-6 left-0 w-full"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>

                    {/* DISCOUNTED PRICE */}
                    <p className="text-white text-5xl font-bold">
                      ₹{discountValue.toLocaleString("en-IN")}
                    </p>
                    {/* SCROLL SECTION - Positioned at bottom aligned with right image */}
                    <div className="md:mt-0 lg:pt-0">
                      <div className="w-full flex flex-row items-center justify-start lg:justify-between gap-3">
                        <div className="flex items-center justify-center">
                          <p className="text-white text-sm lg:text-base whitespace-nowrap mr-1 ml-2" style={{fontSize:10}}>Scroll</p>
                          <img src={scrolldown} className="w-4 h-4 lg:w-4 lg:h-4 bg-transparent mr-8 mt-0" />

                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* -------------------------------------------------------- */}
            {/* RIGHT COLUMN - GRAPH/IPHONE (DESKTOP FIX APPLIED)       */}
            {/* -------------------------------------------------------- */}
            <div
              className="
                w-full 
                mt-0 md:mt-0 
                flex justify-center items-end
                relative
                border-2 rounded-2xl border-transparent
                h-full
                overflow-hidden
              "
            >
              {frame === 0 && (
                <Lottie
                  options={{
                    loop: false,
                    autoplay: inView,
                    animationData: Graph1,
                  }}
                  isClickToPauseDisabled={true}
                  className="
                    w-full max-w-[420px] 
                    md:max-w-[500px] 
                    md:max-h-[60vh] 
                    object-contain
                  "
                />
              )}

              {frame === 1 && (
                <div 
                  className="
                      relative        
                      // top-20
                      md:top-0          
                      md:static       
                      md:top-auto      
                      md:flex
                      "
                >
                  <Lottie
                    options={{
                      loop: false,
                      autoplay: frame === 1,
                      animationData: Graph2,
                    }}
                    isClickToPauseDisabled={true}
                
                    // className="
                    //   md:w-full 
                    //   md:max-w-[500px]
                    //   md:max-h-[60vh]
                    // "
                  />
                </div>
              )}

            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};


// // InvestmentReturns.tsx
// import React, { useEffect, useRef, useState } from "react";
// import { motion, useScroll } from "framer-motion";
// import Lottie from "react-lottie";
// import { useInView } from "react-intersection-observer";

// import Graph1 from "@/assets/New-Graph.json";
// import Graph2 from "@/assets/Iphone.json";
// import Strikeoff from "@/assets/Strikeoff.svg";

// // ------------------------------ CONFIG ------------------------------
// const GROWTH_VALUE = 31;
// const DISCOUNT_VALUE = 95000;
// const ORIGINAL_VALUE = 100000;

// export const InvestmentReturns = () => {
//   const [frame, setFrame] = useState(0);

//   const [growthValue, setGrowthValue] = useState(0);
//   const [discountValue, setDiscountValue] = useState(0);

//   const { ref: cardRef, inView } = useInView({
//     threshold: 0.55,
//     triggerOnce: false,
//   });

//   const sectionRef = useRef<HTMLDivElement | null>(null);

//   const { scrollYProgress } = useScroll({
//     target: sectionRef,
//     offset: ["start start", "end start"],
//   });

//   // ------------------------ FRAME ENGINE ------------------------------
//   useEffect(() => {
//     return scrollYProgress.on("change", (p) => {
//       setFrame(p < 0.20 ? 0 : 1);
//     });
//   }, []);

//   // -------------------- GROWTH VALUE ANIMATION ------------------------
//   useEffect(() => {
//     if (frame !== 0 || !inView) return setGrowthValue(GROWTH_VALUE);

//     setGrowthValue(0);
//     let num = 0;

//     const interval = setInterval(() => {
//       num++;
//       setGrowthValue(num);
//       if (num >= GROWTH_VALUE) clearInterval(interval);
//     }, 40);

//     return () => clearInterval(interval);
//   }, [frame, inView]);

//   // -------------------- PRICE VALUE ANIMATION -------------------------
//   useEffect(() => {
//     if (frame !== 1) return setDiscountValue(0);

//     let current = 0;
//     const step = Math.ceil(DISCOUNT_VALUE / 20);

//     const interval = setInterval(() => {
//       current += step;
//       if (current >= DISCOUNT_VALUE) {
//         current = DISCOUNT_VALUE;
//         clearInterval(interval);
//       }
//       setDiscountValue(current);
//     }, 50);

//     return () => clearInterval(interval);
//   }, [frame]);

//   // ======================= FADE VARIANTS ==========================
//   const fade = {
//     hidden: { opacity: 0, y: 10 },
//     show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
//   };

//   // ---------------------------- JSX -----------------------------------
//   return (
//     <section
//       ref={sectionRef}
//       className="relative w-full"
//       style={{ height: "250vh" }}
//     >
//       <div className="sticky top-[12vh] md:top-[18vh] w-full">
//         <motion.div
//           ref={cardRef}
//           className="bg-[#1D1D21] rounded-2xl w-full overflow-hidden mx-auto h-[80vh] md:h-[75vh]"
//         >
//           <div className="flex flex-col md:grid md:grid-cols-2 h-full">
//             {/* -------------------------------------------------------- */}
//             {/* LEFT COLUMN (NOW WITH BEAUTIFUL FADE ANIMATIONS)        */}
//             {/* -------------------------------------------------------- */}
//             <div className="flex flex-col justify-between md:justify-around px-5 md:px-10 pt-6 pb-4">

//               {/* FRAME 0 CONTENT */}
//               {frame === 0 && (
//                 <motion.div
//                   key="frame0"
//                   initial="hidden"
//                   animate="show"
//                   exit="hidden"
//                   variants={fade}
//                   className="flex flex-col justify-between h-full md:h-5/6"
//                 >
//                   <div>
//                     <h2 className="text-white text-2xl md:text-4xl font-semibold leading-tight">
//                       Supercharge Your <br /> Investment Returns
//                     </h2>

//                     <div className="bg-[#DBAD71] h-[2px] w-[180px] my-3" />

//                     <p className="text-white/70 text-sm max-w-md">
//                       Investing in MFs not only grows your wealth it also
//                       unlocks perks you don’t get anywhere else.
//                     </p>
//                   </div>

//                   <div>
//                     <p className="text-white text-lg">Growth In Last 2 Yrs</p>
//                     <p className="text-[#DBAD71] text-6xl font-bold">
//                       {growthValue}%
//                     </p>
//                     <p className="text-white/60 text-xs mt-2">
//                       * This data is gathered from historic index fund returns
//                     </p>
//                   </div>
//                 </motion.div>
//               )}

//               {/* FRAME 1 CONTENT */}
//               {frame === 1 && (
//                 <motion.div
//                   key="frame1"
//                   initial="hidden"
//                   animate="show"
//                   exit="hidden"
//                   variants={fade}
//                   // className="flex flex-col justify-between h-full"
//                   className="flex flex-col justify-between md:justify-around h-full md:h-5/6"

//                 >
//                   <div>
//                     <h2 className="text-white text-2xl md:text-4xl font-semibold leading-tight">
//                       And Unlock Discounts + <br /> Zero Cost EMIs
//                     </h2>

//                     <div className="bg-[#DBAD71] h-[2px] w-[180px] my-3" />

//                     <p className="text-white/70 text-sm max-w-md">
//                       Pledge your Mutual Funds and instantly unlock a sweet
//                       discount. Same phone, same specs, just lighter on your
//                       wallet.
//                     </p>
//                   </div>

//                   <div className="space-y-2">
//                     {/* ORIGINAL PRICE */}
//                     <div className="relative inline-block">
//                       <p className="text-white text-4xl font-bold opacity-60">
//                         ₹{ORIGINAL_VALUE.toLocaleString("en-IN")}
//                       </p>

//                       <motion.img
//                         src={Strikeoff}
//                         className="absolute top-6 left-0 w-full"
//                         initial={{ scaleX: 0 }}
//                         animate={{ scaleX: 1 }}
//                         transition={{ duration: 0.4 }}
//                       />
//                     </div>

//                     {/* DISCOUNTED PRICE */}
//                     <p className="text-white text-5xl font-bold">
//                       ₹{discountValue.toLocaleString("en-IN")}
//                     </p>
//                   </div>
//                 </motion.div>
//               )}
//             </div>

//             {/* -------------------------------------------------------- */}
//             {/* RIGHT COLUMN - GRAPH / IPHONE                           */}
//             {/* -------------------------------------------------------- */}
//             <div
//               className="
//                 w-full 
//                 mt-6 md:mt-0 
//                 flex justify-center items-end
//                 relative
//               "
//             >
//               {frame === 0 && (
//                 <Lottie
//                   options={{
//                     loop: false,
//                     autoplay: inView,
//                     animationData: Graph1,
//                   }}
//                   className="w-full max-w-[420px] md:max-w-none"
//                 />
//               )}

//               {frame === 1 && (
//                 <Lottie
//                   options={{
//                     loop: false,
//                     autoplay:frame === 1? true:false,
//                     animationData: Graph2,
                  
//                   }}
//                   className="
//                     w-[85%] sm:w-[75%] md:w-full
//                     max-w-[500px]
//                     object-contain mx-auto md:ml-auto
//                   "
//                 />
//               )}
//             </div>
//           </div>
//         </motion.div>
//       </div>
//     </section>
//   );
// };

// // InvestmentReturns.tsx (FINAL 2-FRAME VERSION)
// import React, { useEffect, useRef, useState } from "react";
// import { motion, useTransform, useScroll } from "framer-motion";
// import Lottie from "react-lottie";
// import { useInView } from "react-intersection-observer";

// import Graph1 from "@/assets/Graph.json";
// import Graph2 from "@/assets/Iphone.json";
// import Strikeoff from "@/assets/Strikeoff.svg";

// /* ----------------------------- TYPES & DATA ----------------------------- */

// interface InvestmentScreen {
//   id: string;
//   title: string;
//   subtitle: string;
//   value: number;
//   originalValue?: number;
//   graph: object;
// }

// const screens: InvestmentScreen[] = [
//   {
//     id: "growth",
//     title: "Supercharge Your <br /> Investment Returns",
//     subtitle:
//       "Investing in MFs not only grows your wealth it also unlocks perks you don't get anywhere else.",
//     value: 31,
//     graph: Graph1,
//   },
//   {
//     id: "savings",
//     title: "And Unlock Discounts + <br />Zero Cost EMIs",
//     subtitle:
//       "Pledge your Mutual Funds and instantly unlock a sweet discount. Same phone, same specs, just lighter on your wallet.",
//     value: 95000,
//     originalValue: 100000,
//     graph: Graph2,
//   },
// ];

// /* ----------------------------- FRAME CONFIG ----------------------------- */

// const TOTAL_FRAMES = 2; // <--- ONLY 2 FRAMES NOW!

// /* ----------------------------- FONT SIZES ----------------------------- */

// const FONT = {
//   title: "text-xl md:text-3xl lg:text-6xl",
//   subtitle: "text-xs md:text-base",
//   growthValue: "text-2xl md:text-9xl",
//   priceOriginal: "text-lg md:text-5xl lg:text-8xl",
//   priceFinal: "text-3xl md:text-7xl lg:text-9xl",
// };

// /* ----------------------------- COMPONENT ----------------------------- */

// export const InvestmentReturns: React.FC = () => {
//   const [frame, setFrame] = useState<number>(0);

//   const [growthValue, setGrowthValue] = useState(0);
//   const [priceValue, setPriceValue] = useState(0);

//   const sectionRef = useRef<HTMLDivElement | null>(null);

//   /* 📌 detect when card is visible (for starting frame 0 animation) */
//   const { ref: viewRef, inView } = useInView({
//     threshold: 0.6,
//     triggerOnce: false,
//   });

//   /* 📌 Scroll → map to 0 or 1 only */
//   const { scrollYProgress } = useScroll({
//     target: sectionRef,
//     offset: ["start start", "end end"],
//   });

//   useEffect(() => {
//     const unsub = scrollYProgress.onChange((p) => {
//       const next = p > 0.5 ? 1 : 0;
//       setFrame(next);
//     });
//     return () => unsub();
//   }, []);

//   /* ---------------- GROWTH 0 → 31% (FRAME 0) ---------------- */
//   useEffect(() => {
//     if (frame !== 0 || !inView) {
//       setGrowthValue(frame === 1 ? 31 : 0);
//       return;
//     }

//     let v = 0;
//     let interval = setInterval(() => {
//       v++;
//       setGrowthValue(v);
//       if (v >= screens[0].value) clearInterval(interval);
//     }, 50);

//     return () => clearInterval(interval);
//   }, [frame, inView]);

//   /* ---------------- PRICE DROP (FRAME 1) ---------------- */
//   useEffect(() => {
//     if (frame !== 1) {
//       setPriceValue(0);
//       return;
//     }

//     let v = 0;
//     const target = screens[1].value;
//     const step = Math.ceil(target / 20);

//     let interval = setInterval(() => {
//       v += step;
//       if (v >= target) {
//         v = target;
//         clearInterval(interval);
//       }
//       setPriceValue(v);
//     }, 60);

//     return () => clearInterval(interval);
//   }, [frame]);

//   /* ---------------- CARD SCALE ---------------- */
//   const cardScale = useTransform(scrollYProgress, [0, 1], [1, 0.96]);

//   /* ----------------------------- JSX ----------------------------- */

//   return (
//     <section
//       ref={sectionRef}
//       className="relative w-full"
//       style={{ height: "250vh" }} // only 2 scroll states needed
//     >
//       <div className="sticky top-[15vh] md:top-[20vh] w-full">
//         <motion.div
//           ref={viewRef}
//           className="bg-[#1D1D21] rounded-2xl w-full overflow-hidden"
//           style={{ scale: cardScale, height: "75vh" }}
//           initial={{ opacity: 0, y: 25 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.5 }}
//         >
//           <div className="grid grid-cols-1 md:grid-cols-2 h-full p-0 md:p-0">

//             {/* ---------------- LEFT SIDE ---------------- */}
//             <div className="flex flex-col justify-between p-6">

//               {/* TITLE */}
//               <div className="relative">
//                 {frame === 0 && (
//                   <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
//                     <h2
//                       className={`${FONT.title} text-white`}
//                       dangerouslySetInnerHTML={{ __html: screens[0].title }}
//                     />
//                     <div className="h-[3px] w-[200px] bg-[#DBAD71] my-4"></div>
//                     <p
//                       className={`${FONT.subtitle} text-white/70`}
//                       dangerouslySetInnerHTML={{ __html: screens[0].subtitle }}
//                     />
//                   </motion.div>
//                 )}

//                 {frame === 1 && (
//                   <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
//                     <h2
//                       className={`${FONT.title} text-white`}
//                       dangerouslySetInnerHTML={{ __html: screens[1].title }}
//                     />
//                     <div className="h-[3px] w-[200px] bg-[#DBAD71] my-4"></div>
//                     <p
//                       className={`${FONT.subtitle} text-white/70`}
//                       dangerouslySetInnerHTML={{ __html: screens[1].subtitle }}
//                     />
//                   </motion.div>
//                 )}
//               </div>

//               {/* ---------------- VALUES ---------------- */}
//               <div className="mt-6">

//                 {/* FRAME 0 VALUE */}
//                 {frame === 0 && (
//                   <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
//                     <p className="text-white/80 mb-2 text-lg">
//                       Growth In Last 2 Yrs
//                     </p>
//                     <div
//                       className={`${FONT.growthValue} font-bold text-[#DBAD71]`}
//                     >
//                       {growthValue}%
//                     </div>
//                     <p className="text-white/40 text-sm mt-2">
//                       * This data is gathered from historic index fund returns
//                     </p>
//                   </motion.div>
//                 )}

//                 {/* FRAME 1 VALUE */}
//                 {frame === 1 && (
//                   <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
//                     {/* ORIGINAL PRICE */}
//                     <div
//                       className={`relative ${FONT.priceOriginal} text-white font-bold`}
//                     >
//                       ₹{screens[1].originalValue!.toLocaleString("en-IN")}
//                       <img
//                         src={Strikeoff}
//                         className="absolute inset-0 w-full h-full object-contain"
//                       />
//                     </div>

//                     {/* FINAL PRICE */}
//                     <div
//                       className={`${FONT.priceFinal} text-white font-bold mt-3`}
//                     >
//                       ₹{priceValue.toLocaleString("en-IN")}
//                     </div>
//                   </motion.div>
//                 )}
//               </div>
//             </div>

//             {/* ---------------- RIGHT SIDE ---------------- */}
//             <div className="relative w-full h-full overflow-hidden">

//               {/* FRAME 0 → GROWTH GRAPH */}
//               {frame === 0 && (
//                 <motion.div
//                   className="absolute inset-0"
//                   animate={{ opacity: 1 }}
//                   initial={{ opacity: 0 }}
//                 >
//                   <Lottie
//                     options={{
//                       loop: false,
//                       autoplay: inView && frame === 0,
//                       animationData: screens[0].graph,
//                     }}
//                     className="w-full h-full"
//                   />
//                 </motion.div>
//               )}

//               {/* FRAME 1 → SAVINGS GRAPH */}
//               {frame === 1 && (
//                 <motion.div
//                   className="absolute inset-0"
//                   animate={{ opacity: 1 }}
//                   initial={{ opacity: 0 }}
//                 >
//                   <Lottie
//                     options={{
//                       loop: false,
//                       autoplay: frame === 1,
//                       animationData: screens[1].graph,
//                     }}
//                     className="w-full h-full"
//                   />
//                 </motion.div>
//               )}

//             </div>
//           </div>
//         </motion.div>
//       </div>
//     </section>
//   );
// };

// export default InvestmentReturns;


// // InvestmentReturns.tsx
// import React, { useEffect, useRef, useState } from "react";
// import { motion, useTransform, useScroll } from "framer-motion";
// import Lottie from "react-lottie";
// import { useInView } from "react-intersection-observer";

// import Graph1 from "@/assets/Graph.json";
// import Graph2 from "@/assets/Iphone.json";
// import Strikeoff from "@/assets/Strikeoff.svg";

// /* ----------------------------- Types & Data ----------------------------- */
// interface InvestmentScreen {
//   id: string;
//   title: string;
//   subtitle: string;
//   value: number;
//   originalValue?: number;
//   graph: object;
// }

// const investmentScreens: InvestmentScreen[] = [
//   {
//     id: "growth",
//     title: "Supercharge Your <br /> Investment Returns",
//     subtitle:
//       "Investing in MFs not only grows your wealth it also unlocks perks you don't get anywhere else.",
//     value: 31,
//     graph: Graph1,
//   },
//   {
//     id: "savings",
//     title: "And Unlock Discounts + <br />Zero Cost EMIs",
//     subtitle:
//       "Pledge your Mutual Funds and instantly unlock a sweet discount. Same phone, same specs, just lighter on your wallet.",
//     value: 95000,
//     originalValue: 100000,
//     graph: Graph2,
//   },
// ];

// /* ----------------------------- Config & Styles -------------------------- */
// const TOTAL_FRAMES = 8; // keep same mapping as your previous design (0..7)
// const ANIMATION_CONFIG = { totalFrames: TOTAL_FRAMES };

// const FONT_SIZES = {
//   title: "text-xl md:text-3xl lg:text-6xl",
//   subtitle: "text-xs md:text-base",
//   growthLabel: "text-xs md:text-3xl",
//   growthValue: "text-2xl md:text-9xl lg:text-9xl",
//   originalValue: "text-lg md:text-5xl lg:text-9xl",
//   discountedValue: "text-2xl md:text-6xl lg:text-10xl",
//   barHeight: "h-[1px] md:h-[4px]",
//   strikeHeight: "h-1 md:h-2 lg:h-3",
// };

// /* ----------------------------- Component ----------------------------- */
// export const InvestmentReturns: React.FC = () => {
//   // Frame engine state
//   const [currentFrame, setCurrentFrame] = useState<number>(0);

//   // Animated values
//   const [displayedGrowthValue, setDisplayedGrowthValue] = useState<number>(0);
//   const [displayedPriceValue, setDisplayedPriceValue] = useState<number>(0);

//   // Price animation completion (to lock scroll)
//   const [priceAnimationComplete, setPriceAnimationComplete] =
//     useState<boolean>(false);

//   // refs
//   const sectionRef = useRef<HTMLDivElement | null>(null);
//   const containerRef = useRef<HTMLDivElement | null>(null);

//   // "card visible in viewport" detection
//   // const { ref: cardInViewRef, inView: cardInView } = useInView({
//   //   threshold: 0.3, // ~30% of card visible
//   //   triggerOnce: false,
//   // });

//   const { ref: cardInViewRef, inView: cardInView } = useInView({
//     threshold: 0.7,          // 60% of card must be visible
//     rootMargin: "-20% 0px -20% 0px", // delays trigger until card is centered
//     triggerOnce: false,
//   });
//   // framer scroll
//   const { scrollYProgress } = useScroll({
//     target: sectionRef,
//     offset: ["start start", "end end"],
//   });

//   /* ---------------------- map scroll progress -> frame --------------------- */
//   useEffect(() => {
//     const unsubscribe = scrollYProgress.onChange((progress) => {
//       const rawFrame = Math.floor(progress * ANIMATION_CONFIG.totalFrames);
//       const clamped = Math.min(rawFrame, ANIMATION_CONFIG.totalFrames - 1);

//       // if progress tries to advance beyond frame 6
//       // but the price animation hasn't finished, hold at frame 6
//       if (clamped > 6 && !priceAnimationComplete) {
//         setCurrentFrame(6);
//       } else {
//         setCurrentFrame(clamped);
//       }
//     });

//     return () => unsubscribe();
//   }, [scrollYProgress, priceAnimationComplete]);

//   /* ------------------- helpers for frame checks (readability) -------------- */
//   const isFrame = (f: number) => currentFrame === f;
//   const atLeast = (f: number) => currentFrame >= f;
//   const between = (a: number, b: number) =>
//     currentFrame >= a && currentFrame <= b;

//   /* ----------------------- Growth percentage animation --------------------- */
//   useEffect(() => {
//     const target = investmentScreens[0].value; // 31
//     const totalTimeMs = 2800;
//     const steps = target || 1;
//     const intervalMs = Math.max(10, Math.floor(totalTimeMs / steps));

//     let interval: ReturnType<typeof setInterval> | null = null;

//     // ✅ Start 0–31% animation when:
//     // - the card is in view
//     // - AND we're on the first frame (0)
//     if (cardInView && isFrame(0)) {
//       setDisplayedGrowthValue(0);

//       interval = setInterval(() => {
//         setDisplayedGrowthValue((prev) => {
//           if (prev < target) return prev + 1;
//           if (interval) clearInterval(interval);
//           return target;
//         });
//       }, intervalMs);
//     } else if (!cardInView || !isFrame(0)) {
//       // Reset when card fully out of view OR we moved to another frame
//       if(displayedGrowthValue !==0){
//         setDisplayedGrowthValue(31);

//       }
//       // debugger
//     }

//     return () => {
//       if (interval) clearInterval(interval);
//     };
//   }, [cardInView, currentFrame]);

//   /* ----------------------- Price counting animation (frame 6) ------------- */
//   useEffect(() => {
//     const target = investmentScreens[1].value;
//     let interval: ReturnType<typeof setInterval> | null = null;

//     if (isFrame(6)) {
//       // entering frame 6 - reset flag & value
//       setPriceAnimationComplete(false);
//       setDisplayedPriceValue(0);

//       const steps = 15;
//       const stepValue = Math.ceil(target / steps);
//       const stepDelay = 100; // ms

//       interval = setInterval(() => {
//         setDisplayedPriceValue((prev) => {
//           const next = prev + stepValue;
//           if (next >= target) {
//             if (interval) clearInterval(interval);
//             setPriceAnimationComplete(true);
//             return target;
//           }
//           return next;
//         });
//       }, stepDelay);
//     } else if (currentFrame < 6) {
//       // leaving back before 6 - reset
//       setDisplayedPriceValue(0);
//       setPriceAnimationComplete(false);
//     }

//     return () => {
//       if (interval) clearInterval(interval);
//     };
//   }, [currentFrame]);

//   /* ----------------------- small card scale transform ---------------------- */
//   const cardScale = useTransform(scrollYProgress, [0, 1], [1, 0.98]);

//   /* ---------------------- className shortcuts ------------------------------- */
//   const titleFontClass = `font-medium md:font-medium lg:font-semibold ${FONT_SIZES.title} text-[#FFFFFF]`;
//   const subtitleTextClass = `font-regular text-[#FFFFFF99] max-w-xl pr-4 ${FONT_SIZES.subtitle}`;
//   const valueTextClass = "font-bold text-[#DBAD71] mt-0";

//   /* ----------------------------- JSX ------------------------------------- */
//   return (
//     <section
//       ref={sectionRef}
//       className="relative w-full"
//       style={{ height: "400vh" }} // preserve original sticky intent
//     >
//       <div className="sticky md:top-[20vh] top-[15vh] w-full">
//         <div className="w-full mx-auto">
//           <motion.div
//             ref={(node) => {
//               // hook both framer-motion and useInView into this card
//               containerRef.current = node;
//               cardInViewRef(node);
//             }}
//             className="bg-[#1D1D21] rounded-2xl w-full overflow-hidden"
//             style={{ scale: cardScale, height: "75vh" }}
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.5 }}
//           >
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-0 pl-4 pt-4 pb-0 md:pl-0 pt-10 h-full">
//               {/* LEFT COLUMN */}
//               <div className="flex flex-col justify-between md:pl-10">
//                 {/* TITLE / SUBTITLE */}
//                 <div className="mb-4 md:mb-4 relative">
//                   {/* Title block for frames 0..2 */}
//                   <motion.div
//                     initial={{ opacity: 1 }}
//                     animate={{ opacity: atLeast(3) ? 0 : 1 }}
//                     transition={{ duration: 0.4 }}
//                   >
//                     <h2
//                       className={`${titleFontClass} mb-4 md:mb-2`}
//                       style={{ lineHeight: 1.2 }}
//                       dangerouslySetInnerHTML={{
//                         __html: investmentScreens[0].title,
//                       }}
//                     />
//                     <div
//                       className={`bg-[#DBAD71] w-[200px] ${FONT_SIZES.barHeight} rounded-[19px] opacity-100 mb-4`}
//                     ></div>

//                     <p
//                       className={subtitleTextClass}
//                       dangerouslySetInnerHTML={{
//                         __html: investmentScreens[0].subtitle,
//                       }}
//                     />
//                   </motion.div>

//                   {/* Title block for frame 7+ (final title) */}
//                   <motion.div
//                     className="absolute top-0 w-full"
//                     initial={{ opacity: 0 }}
//                     animate={{ opacity: atLeast(7) ? 1 : 0 }}
//                     transition={{ duration: 0.4 }}
//                   >
//                     <h2
//                       className={`${titleFontClass} mb-4 md:mb-2 pt-4`}
//                       style={{ lineHeight: 1.2 }}
//                       dangerouslySetInnerHTML={{
//                         __html: investmentScreens[1].title,
//                       }}
//                     />
//                     {atLeast(7) && (
//                       <div
//                         className={`bg-[#DBAD71] w-[200px] ${FONT_SIZES.barHeight} rounded-[19px] opacity-100 mb-4`}
//                       ></div>
//                     )}

//                     <p
//                       className={`${subtitleTextClass}`}
//                       dangerouslySetInnerHTML={{
//                         __html: investmentScreens[1].subtitle,
//                       }}
//                     />
//                   </motion.div>
//                 </div>

//                 {/* VALUES SECTION - switch between growth & savings */}
//                 <div className="space-y-1 md:space-y-2 pb-4 md:pb-8 mt-4 md:mt-4">
//                   {/* Growth display (frames 0..2) */}
//                   {currentFrame < 3 && (
//                     <motion.div
//                       initial={{ opacity: 1 }}
//                       animate={{ opacity: 1 }}
//                       transition={{ duration: 0.3 }}
//                     >
//                       <p
//                         className={`font-medium text-[#FFFFFF] mb-0 md:mb-2 ${FONT_SIZES.growthLabel}`}
//                       >
//                         Growth In Last 2 Yrs
//                       </p>
//                       <div
//                         className={`${valueTextClass} ${FONT_SIZES.growthValue}`}
//                       >
//                         {`${displayedGrowthValue}%`}
//                       </div>
//                       <motion.p
//                         className={`text-white/70 mt-2 ${FONT_SIZES.subtitle}`}
//                         initial={{ opacity: 0 }}
//                         animate={{ opacity: currentFrame === 0 ? 1 : 0 }}
//                         transition={{ duration: 0.3 }}
//                       >
//                         * This data is gathered from historic index fund returns
//                       </motion.p>
//                     </motion.div>
//                   )}

//                   {/* Savings / Price display (frames 3+) */}
//                   {atLeast(3) && (
//                     <motion.div
//                       initial={{ opacity: 0 }}
//                       animate={{ opacity: 1 }}
//                       transition={{ duration: 0.4 }}
//                     >
//                       {/* Original price (visible from frame 4) */}
//                       {atLeast(4) && (
//                         <motion.div
//                           className="relative inline-flex items-center"
//                           initial={{ opacity: 0 }}
//                           animate={{ opacity: 1 }}
//                           transition={{ duration: 0.3 }}
//                         >
//                           <span
//                             className={`font-bold text-[#FFFFFF] ${FONT_SIZES.originalValue}`}
//                           >
//                             ₹
//                             <span className="ml-1 sm:ml-2">
//                               {investmentScreens[1].originalValue!.toLocaleString(
//                                 "en-IN",
//                               )}
//                             </span>
//                           </span>

//                           {/* Strike overlay - appears from frame 5 */}
//                           {atLeast(5) && (
//                             <motion.div
//                               className="absolute inset-0 flex items-center justify-center pointer-events-none"
//                               initial={{ scaleX: 0 }}
//                               animate={{ scaleX: 1 }}
//                               transition={{ duration: 0.3 }}
//                             >
//                               <img
//                                 src={Strikeoff}
//                                 alt="strikethrough"
//                                 className={`w-full object-contain ${FONT_SIZES.strikeHeight}`}
//                               />
//                             </motion.div>
//                           )}
//                         </motion.div>
//                       )}

//                       {/* Discounted value (visible from frame 6, reserved space from 4) */}
//                       {atLeast(4) && (
//                         <motion.div
//                           className={`font-bold text-[#FFFFFF] ${FONT_SIZES.discountedValue}`}
//                           initial={{ opacity: 0 }}
//                           animate={{ opacity: atLeast(6) ? 1 : 0 }}
//                           transition={{ duration: 0.3 }}
//                         >
//                           ₹
//                           <span className="ml-1 sm:ml-2 md:ml-3">
//                             {displayedPriceValue.toLocaleString("en-IN")}
//                           </span>
//                         </motion.div>
//                       )}
//                     </motion.div>
//                   )}
//                 </div>
//               </div>

//               {/* RIGHT COLUMN - Graphs / Lottie */}
//            {/* RIGHT COLUMN - Graphs / Lottie */}
// <div className="relative rounded-2xl w-full overflow-hidden
//     h-[300px] sm:h-[340px] md:h-[380px] lg:h-full w-full">

//   {/* -------- FRAME 0–2 : GROWTH GRAPH -------- */}
//   {currentFrame < 3 && (
//     <motion.div
//       className="absolute inset-0"
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       exit={{ opacity: 0 }}
//       transition={{ duration: 0.35 }}
//     >
//       <Lottie
//         options={{
//           loop: false,
//           autoplay: cardInView && currentFrame < 3,
//           animationData: investmentScreens[0].graph,
//           rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
//         }}
//         isStopped={!(cardInView && currentFrame < 3)}
//         className="w-full h-full"
//       />
//     </motion.div>
//   )}

//   {/* -------- FRAME 3+ : SAVINGS GRAPH -------- */}
//   {currentFrame >= 3 && (
//     <motion.div
//       className="absolute inset-0"
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       exit={{ opacity: 0 }}
//       transition={{ duration: 0.35 }}
//     >
//       <Lottie
//         options={{
//           loop: false,
//           autoplay: isFrame(3),
//           animationData: investmentScreens[1].graph,
//           rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
//         }}
//         className="w-full h-full"
//       />
//     </motion.div>
//   )}

// </div>

//             </div>
//           </motion.div>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default InvestmentReturns;
